import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import type { SortingState } from '@/components/shared/DataTable/types';
import {
  useRestorePrefsToSearch,
  useTablePreferences,
} from './useTablePreferences';

export interface UseTableOptions {
  /**
   * Remap DataTable column IDs → API sort field names.
   * e.g. { retracedRiskScore: 'riskScore' }
   */
  sortKeyMap?: Record<string, string>;
  /**
   * When set, restore-from-prefs is skipped unless the active tab matches.
   */
  tab?: string;
  /**
   * Sort applied when neither the URL/local state nor saved prefs specify one.
   * Format: `field:ASC` / `field:DESC` (the API sort-field name, already mapped).
   * Lets a table load pre-sorted without the user clicking a header.
   */
  defaultSort?: string;
  /**
   * Where to keep table state.
   *  - `'url'` *(default)* — page/limit/sort/filters live in the route's
   *    search params; deep-linkable and survive refresh.
   *  - `'state'` — local React state on the hook caller; URL stays clean.
   *    Use for embedded / secondary tables (e.g. version-history below the
   *    audit info card, related-actions inside a sheet).
   */
  mode?: 'url' | 'state';
  /** Initial values when `mode === 'state'`. Ignored in URL mode. */
  initialState?: {
    page?: number;
    limit?: number;
    sort?: string;
    search?: string;
    filters?: Record<string, string[]>;
  };
}

interface AnySearch {
  tab?: string;
  page?: number;
  limit?: number;
  sort?: string;
  search?: string;
  filters?: Record<string, string[]>;
}

function buildFilterParam(filters: Record<string, string[]>): string {
  const out: Record<string, string> = {};
  for (const [key, vals] of Object.entries(filters)) {
    if (vals && vals.length > 0) out[key] = vals.join(',');
  }
  return JSON.stringify(out);
}

export function useTable(storageKey: string, opts: UseTableOptions = {}) {
  const {
    sortKeyMap = {},
    tab,
    defaultSort,
    mode = 'url',
    initialState = {},
  } = opts;

  // --- Local-state mode ------------------------------------------------------
  // Self-contained: no URL, no prefs restore. Caller's component owns the
  // state; sibling tables on the same route stay independent.
  const [localPage, setLocalPage] = useState(initialState.page ?? 1);
  const [localLimit, setLocalLimit] = useState(initialState.limit ?? 50);
  const [localSort, setLocalSort] = useState<string | undefined>(
    initialState.sort,
  );
  const [localSearch, setLocalSearch] = useState(initialState.search ?? '');
  const [localFilters, setLocalFilters] = useState<Record<string, string[]>>(
    initialState.filters ?? {},
  );

  // --- URL-mode plumbing -----------------------------------------------------
  const routeSearch = (useSearch({ strict: false }) ?? {}) as AnySearch;
  const navigate = useNavigate();
  const { save: savePrefs } = useTablePreferences(storageKey);

  const hasExistingSearch = tab
    ? (routeSearch.tab !== undefined && routeSearch.tab !== tab) ||
      routeSearch.sort !== undefined ||
      routeSearch.filters !== undefined
    : routeSearch.sort !== undefined || routeSearch.filters !== undefined;

  useRestorePrefsToSearch(storageKey, {
    hasExistingSearch,
    enabled: mode === 'url',
    mapPrefs: prefs => {
      const sortStr = prefs.sorting
        ? (() => {
            try {
              const parsed = JSON.parse(prefs.sorting) as SortingState;
              const first = parsed[0];
              if (!first) return undefined;
              const apiField = sortKeyMap[first.id] ?? first.id;
              return `${apiField}:${first.desc ? 'DESC' : 'ASC'}`;
            } catch {
              return undefined;
            }
          })()
        : undefined;
      const filters = prefs.filters
        ? (() => {
            try {
              return JSON.parse(prefs.filters) as Record<string, string[]>;
            } catch {
              return undefined;
            }
          })()
        : undefined;
      return {
        ...(sortStr !== undefined ? { sort: sortStr } : {}),
        ...(filters !== undefined ? { filters } : {}),
      };
    },
  });

  const isActiveTab =
    !tab || routeSearch.tab === undefined || routeSearch.tab === tab;

  // --- Derived state ---------------------------------------------------------
  const page =
    mode === 'state' ? localPage : isActiveTab ? (routeSearch.page ?? 1) : 1;
  const limit =
    mode === 'state'
      ? localLimit
      : isActiveTab
        ? (routeSearch.limit ?? 50)
        : 50;
  const search = mode === 'state' ? localSearch : (routeSearch.search ?? '');
  const filters: Record<string, string[]> =
    mode === 'state'
      ? localFilters
      : ((isActiveTab ? (routeSearch.filters ?? {}) : {}) as Record<
          string,
          string[]
        >);
  const activeSort = mode === 'state' ? localSort : routeSearch.sort;
  // Fall back to the caller's default sort when nothing explicit is set, so the
  // table loads pre-sorted and the query sends a `sort` param on first render.
  const sortStr = activeSort ?? defaultSort;

  const sorting: SortingState = useMemo(() => {
    const s = mode === 'state' ? sortStr : isActiveTab ? sortStr : undefined;
    if (!s) return [];
    const [field, dir] = s.split(':');
    return field ? [{ id: field, desc: dir === 'DESC' }] : [];
  }, [mode, isActiveTab, sortStr]);

  // navigate is called from generic hooks that don't know the route's search schema.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = navigate as unknown as (opts: {
    search: (prev: any) => any;
  }) => void;

  function onSortingChange(next: SortingState) {
    const first = next[0];
    const nextSort = first
      ? `${sortKeyMap[first.id] ?? first.id}:${first.desc ? 'DESC' : 'ASC'}`
      : undefined;
    if (mode === 'state') {
      setLocalSort(nextSort);
      setLocalPage(1);
      return;
    }
    if (!first) {
      nav({ search: prev => ({ ...prev, page: 1, sort: undefined }) });
      savePrefs({ sorting: undefined });
      return;
    }
    nav({ search: prev => ({ ...prev, page: 1, sort: nextSort }) });
    savePrefs({ sorting: JSON.stringify(next) });
  }

  function onFiltersChange(next: Record<string, string[]>) {
    const isEmpty = Object.values(next).every(v => !v || v.length === 0);
    if (mode === 'state') {
      setLocalFilters(isEmpty ? {} : next);
      setLocalPage(1);
      return;
    }
    nav({
      search: prev => ({
        ...prev,
        page: 1,
        filters: isEmpty ? undefined : next,
      }),
    });
    savePrefs({ filters: isEmpty ? undefined : JSON.stringify(next) });
  }

  function onPaginationChange(v: { pageIndex: number; pageSize: number }) {
    if (mode === 'state') {
      setLocalPage(v.pageIndex + 1);
      setLocalLimit(v.pageSize);
      return;
    }
    nav({
      search: prev => ({ ...prev, page: v.pageIndex + 1, limit: v.pageSize }),
    });
    savePrefs({ rowsPerPage: v.pageSize });
  }

  function onSearchChange(next: string) {
    if (mode === 'state') {
      setLocalSearch(next);
      setLocalPage(1);
      return;
    }
    nav({ search: prev => ({ ...prev, search: next || undefined, page: 1 }) });
  }

  const filterParam =
    Object.keys(filters).length > 0 ? buildFilterParam(filters) : undefined;

  // Ready-to-spread into any data hook. Both `limit` and `count` are included
  // because different APIs use different names for page size.
  const queryParams = {
    page,
    limit,
    count: limit,
    ...(sortStr !== undefined ? { sort: sortStr } : {}),
    ...(filterParam !== undefined ? { filter: filterParam } : {}),
    ...(search ? { search } : {}),
  };

  return {
    sorting,
    filters,
    search,
    page,
    limit,
    onSortingChange,
    onFiltersChange,
    onPaginationChange,
    onSearchChange,
    queryParams,
  };
}
